const PHONE_START_SELECTOR = '.firebaseui-id-page-phone-sign-in-start';
const PHONE_CODE_SELECTOR = '.firebaseui-id-page-phone-sign-in-finish';
const enhancedCodeInputs = new WeakSet();

const normalizedCode = text => Array.from(text || '').map(character => {
    const code = character.codePointAt(0);
    for (const zero of [48, 0x660, 0x6f0, 0xff10]) {
        if (code >= zero && code <= zero + 9) return String(code - zero);
    }
    return /[\s-]/u.test(character) ? '' : character;
}).join('');

export const getPhoneSigninOptions = (providerId, compactPhone = false) => (
    compactPhone
        ? [{
            provider: providerId,
            recaptchaParameters: { size: 'invisible', badge: 'bottomright' },
        }]
        : [providerId]
);

const setAttributes = (element, attributes) => {
    if (!element) return;
    Object.entries(attributes).forEach(([name, value]) => {
        if (element.getAttribute(name) !== value) {
            element.setAttribute(name, value);
        }
    });
};

// FirebaseUI owns validation, SMS delivery and the verification session.
// Enhance its generated fields and align known error copy after each render.
export const enhancePhoneSigninUi = (container) => {
    const codePage = container.querySelector(PHONE_CODE_SELECTOR);
    const phonePage = container.querySelector(PHONE_START_SELECTOR);
    const stage = codePage ? 'code' : phonePage ? 'phone' : null;
    if (!stage) return null;

    container.dataset.phoneStage = stage;
    const codeError = codePage?.querySelector('.firebaseui-id-phone-confirmation-code-error');
    if (codeError?.textContent === 'Wrong code. Try again.') {
        codeError.textContent = 'That code is incorrect. Please try again.';
    }
    const header = (codePage || phonePage).querySelector('.firebaseui-card-header');
    if (header) {
        if (container.closest('[data-phone-stage-titles="external"]')) {
            header.setAttribute('aria-hidden', 'true');
        } else {
            header.removeAttribute('aria-hidden');
        }
    }
    setAttributes(container.querySelector('.firebaseui-id-phone-number'), {
        autocomplete: 'tel-national',
        inputmode: 'tel',
        autocapitalize: 'off',
        autocorrect: 'off',
    });
    setAttributes(container.querySelector('.firebaseui-id-phone-confirmation-code'), {
        type: 'text',
        autocomplete: 'one-time-code',
        inputmode: 'numeric',
        pattern: '[0-9]*',
        maxlength: '6',
        autocapitalize: 'off',
        autocorrect: 'off',
        spellcheck: 'false',
    });
    const codeInput = container.querySelector('.firebaseui-id-phone-confirmation-code');
    if (codeInput && !enhancedCodeInputs.has(codeInput)) {
        enhancedCodeInputs.add(codeInput);
        // Read SMS/pasted codes before maxlength can truncate formatting, while
        // leaving FirebaseUI's confirmation, validation and retry session intact.
        codeInput.addEventListener('paste', event => {
            const code = normalizedCode(event.clipboardData?.getData('text'));
            if (!/^\d{6}$/.test(code)) return;
            event.preventDefault();
            codeInput.value = code;
            codeInput.dispatchEvent(new Event('input', { bubbles: true }));
        }, true);
        codeInput.addEventListener('input', () => {
            const code = normalizedCode(codeInput.value);
            if (/^\d{0,6}$/.test(code)) codeInput.value = code;
        }, true);
    }

    if (phonePage) {
        const submit = phonePage.querySelector('.firebaseui-id-submit');
        if (submit && submit.textContent !== 'Send code') {
            submit.textContent = 'Send code';
        }
        const notice = phonePage.querySelector('.firebaseui-phone-sms-notice, .firebaseui-phone-tos');
        if (notice) {
            // Keep any Terms and Privacy links intact when changing the CTA copy.
            Array.from(notice.childNodes).forEach((node) => {
                if (node.nodeType === 3 && node.textContent.includes('By tapping Verify')) {
                    node.textContent = node.textContent.replace('By tapping Verify', 'By tapping Send code');
                }
            });
        }
    }
    return stage;
};

export const observePhoneSigninUi = (container, onStageChange) => {
    let previousStage = null;
    const refresh = () => {
        const stage = enhancePhoneSigninUi(container);
        if (stage && stage !== previousStage) {
            previousStage = stage;
            onStageChange?.(stage);
        }
    };
    const observer = new MutationObserver(refresh);
    observer.observe(container, { childList: true, subtree: true });
    refresh();
    return { refresh, disconnect: () => observer.disconnect() };
};
